import { useMemo, lazy, Suspense, useCallback, useRef, useContext, useEffect } from 'react';
import { debounce } from 'debounce';
import { useIntl } from '@alltrails/shared/react-intl';
import logExploreSearchBoxClicked from '@alltrails/analytics/events/logExploreSearchBoxClicked';
import logSearchResultClicked from '@alltrails/analytics/events/logSearchResultClicked';
import SearchOrigin from '@alltrails/analytics/enums/SearchOrigin';
import SearchSuggestionSelectionType from '@alltrails/analytics/enums/SearchSuggestionSelectionType';
import WebSearchTab from '@alltrails/analytics/enums/WebSearchTab';
import useSearch from 'hooks/useSearch';
import { DESKTOP_BREAKPOINT } from 'utils/constants/breakpoints';
import { ARROW_UP, ARROW_DOWN, ESC } from 'utils/constants/key_codes';
import SearchBar from 'components/search/SearchBar';
import { SearchInsightsContext } from 'components/SearchInsightsProvider';
import { algoliaTypeToAmplitudeType } from 'utils/amplitudeHelper';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import PortalModal from 'components/shared/PortalModal';
import CustomProvider from 'components/CustomProvider';

import * as styles from './HomeSearchBar.module.scss';

const MobileSearchBar = lazy(() => import('../MobileSearchBar'));

type Props = {
  latLng?: (string | number)[];
  windowWidth?: number;
};

export const navigateToSearchResult = (result: { url: string }) => {
  // Create a full URL so that we can properly parse the path and query
  // parameters. The host is irrelevant, but needed for the URL constructor.
  // We need to create a URL object first because we cannot pass a relative
  // URL like /path?someparam=whatever directly to UrlSearchParams(). If we
  // did that it would treat `/path?someparam` as a single key.
  const url = new URL(result.url as string, 'http://placeholder');

  url.searchParams.set('search', 'true');

  // We ship non-standard parameters for some of our results URLs. The list
  // parameter format of `f[]=dogs` does not seem widely supported, and is not
  // what URLSearchParams uses. It seems `f=dogs&f=cats` would be superior. We
  // need to decode what URLSearchParams has escaped on our behalf.
  const searchParams = decodeURIComponent(url.searchParams.toString());

  const searchParamsSegment = searchParams ? `?${searchParams}` : '';

  window.location.href = `${url.pathname}${searchParamsSegment}`;
};

const BaseHomeSearchBar = ({ latLng = null, windowWidth = null }: Props) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const searchContainer = useRef<HTMLDivElement>();

  const analyticsTagsParams = {
    user,
    locale: languageRegionCode,
    origin: SearchOrigin.Homepage,
    mobile: __AT_DATA__.mobileBrowser
  };

  const { updateSearchId, logClickEvent } = useContext(SearchInsightsContext);

  const { searchState, updateQuery, updateActiveTabFilter, toggleEngaged, updateActiveIndexResult } = useSearch({
    latLng,
    languageRegionCode,
    analyticsTagsParams,
    intl
  });

  // Whenever we update the search id inform our global tracking context about it.
  useEffect(() => {
    if (searchState.queryID) {
      updateSearchId(searchState.queryID);
    }
  }, [updateSearchId, searchState.queryID]);

  const handleSearchResultClick = useCallback(
    (result, idx) => {
      const searchTab = searchState.filters[searchState.activeFilterIndex].label;
      const contentId = result?.objectID ? result.objectID.split('-')[1] : '';
      logSearchResultClicked({
        click_position: idx,
        content_id: contentId,
        item_type: algoliaTypeToAmplitudeType(result?.type),
        location: SearchOrigin.Homepage,
        poi_type: null /* TODO: DISCO-604 add poi analytics */,
        query_length: searchState.query ? searchState.query.length : 0,
        selection_type: SearchSuggestionSelectionType.SearchSuggestion,
        search_tab: searchTab as WebSearchTab
      });
      logClickEvent(result?.objectID, idx + 1);
      navigateToSearchResult(result);
    },
    [searchState.query, searchState.filters, searchState.activeFilterIndex, logClickEvent]
  );

  const handleSearchBoxFocus = useCallback(() => {
    logExploreSearchBoxClicked({ search_origin: SearchOrigin.Homepage });
  }, []);

  const isMobileWidth = useMemo(() => {
    return windowWidth ? windowWidth < DESKTOP_BREAKPOINT : true;
  }, [windowWidth]);

  const mobileSearchBar = useMemo(() => isMobileWidth && searchState.engaged, [isMobileWidth, searchState.engaged]);

  const checkForFocusOut = debounce(() => {
    if (searchContainer.current && !searchContainer.current.contains(document.activeElement)) {
      toggleEngaged(false);
    }
  }, 800);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // this handles keyboard interactions, for arrow_up and down as well as esc
    if (window.keyboardUser) {
      checkForFocusOut();
    }

    if ([ARROW_UP, ARROW_DOWN].includes(e.keyCode)) {
      e.preventDefault();
    }

    switch (e.keyCode) {
      case ESC:
        toggleEngaged(false);
        break;
      case ARROW_UP:
        if (searchState.activeIndexResult === 0) {
          updateActiveIndexResult(searchState.results.length - 1);
        } else {
          updateActiveIndexResult(searchState.activeIndexResult - 1);
        }
        break;
      case ARROW_DOWN:
        if (searchState.activeIndexResult + 1 === searchState.results.length) {
          updateActiveIndexResult(0);
        } else {
          updateActiveIndexResult(searchState.activeIndexResult + 1);
        }
        break;
      default:
        // Ignore.
        break;
    }
  };

  return (
    <>
      <PortalModal isOpen={mobileSearchBar}>
        <Suspense fallback={null}>
          <MobileSearchBar
            query={searchState.query}
            loading={searchState.loading}
            updateQuery={updateQuery}
            results={searchState.results}
            filters={searchState.filters}
            activeFilterIndex={searchState.activeFilterIndex}
            updateActiveTabFilter={updateActiveTabFilter}
            toggleEngaged={toggleEngaged}
            activeIndexResult={searchState.activeIndexResult}
            updateActiveIndexResult={updateActiveIndexResult}
            handleSearchResultClick={handleSearchResultClick}
            handleSearchBoxFocus={handleSearchBoxFocus}
          />
        </Suspense>
      </PortalModal>
      <div role="presentation" ref={searchContainer} className={styles.container} onKeyUp={handleKeyUp}>
        <SearchBar
          query={searchState.query}
          loading={searchState.loading}
          updateQuery={updateQuery}
          results={searchState.results}
          filters={searchState.filters}
          activeFilterIndex={searchState.activeFilterIndex}
          updateActiveTabFilter={updateActiveTabFilter}
          toggleEngaged={toggleEngaged}
          engaged={searchState.engaged}
          activeIndexResult={searchState.activeIndexResult}
          updateActiveIndexResult={updateActiveIndexResult}
          handleSearchResultClick={handleSearchResultClick}
          mobileSearchBar={mobileSearchBar}
          handleSearchBoxFocus={handleSearchBoxFocus}
          isMobileWidth={isMobileWidth}
        />
      </div>
    </>
  );
};

export default function HomeSearchBar({ latLng, windowWidth }: Props) {
  return (
    <CustomProvider>
      <BaseHomeSearchBar latLng={latLng} windowWidth={windowWidth} />
    </CustomProvider>
  );
}
